import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Circle,
  ArrowRight,
  FileText,
  Upload,
  MessageCircle,
  Video,
  Calendar,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Target,
  Zap,
  Lock
} from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { toast } from "sonner";

// Define the steps for the client journey
const JOURNEY_STEPS = [
  {
    id: "account",
    title: "Cuenta Creada",
    description: "Tu cuenta está activa",
    icon: CheckCircle2,
    link: null,
    alwaysComplete: true
  },
  {
    id: "documents",
    title: "Subir Documentos",
    description: "Sube tu pasaporte y documentos básicos",
    icon: Upload,
    link: "/documents",
    checkField: "documents_uploaded"
  },
  {
    id: "ds160",
    title: "Completar DS-160",
    description: "Llena el formulario de solicitud de visa",
    icon: FileText,
    link: "/ds160-assistant",
    checkField: "ds160_progress",
    threshold: 100
  },
  {
    id: "interview_prep",
    title: "Practicar Entrevista",
    description: "Prepárate con el simulador de entrevista",
    icon: Video,
    link: "/interview-simulator",
    checkField: "interview_practiced",
    premium: true
  },
  {
    id: "appointment",
    title: "Agendar Cita",
    description: "Agenda tu cita en el consulado",
    icon: Calendar,
    link: null,
    checkField: "appointment_scheduled"
  }
];

const NextStepBanner = ({ caseData, ds160Progress, user, documentsCount }) => {
  // Determine which step is next
  const getNextStep = () => {
    // Check documents
    const hasDocuments = documentsCount > 0;
    if (!hasDocuments) {
      return {
        step: JOURNEY_STEPS[1],
        message: "Tu siguiente paso es subir tus documentos básicos",
        action: "Subir Documentos",
        link: "/documents",
        priority: "high"
      };
    }

    // Check DS-160 progress
    const ds160Complete = ds160Progress >= 100;
    if (!ds160Complete) {
      return {
        step: JOURNEY_STEPS[2],
        message: ds160Progress > 0 
          ? `Continúa llenando tu formulario DS-160 (${ds160Progress}% completado)`
          : "Completa tu formulario DS-160 para tu solicitud de visa",
        action: ds160Progress > 0 ? "Continuar DS-160" : "Iniciar DS-160",
        link: "/ds160-assistant",
        priority: "high"
      };
    }

    // Check interview practice (premium only)
    const isPremium = user?.plan_type === "premium" || user?.plan_type === "vip";
    const hasPracticed = caseData?.interview_practiced;
    if (isPremium && !hasPracticed) {
      return {
        step: JOURNEY_STEPS[3],
        message: "Practica con el simulador de entrevista para aumentar tus probabilidades",
        action: "Practicar Entrevista",
        link: "/interview-simulator",
        priority: "medium"
      };
    }

    // All done!
    return {
      step: null,
      message: "¡Excelente! Has completado todos los pasos. Mantente en contacto con tu asesor.",
      action: "Ver mi Progreso",
      link: null,
      priority: "success"
    };
  };

  const nextStep = getNextStep();

  const priorityStyles = {
    high: "from-emerald-500 to-teal-500",
    medium: "from-blue-500 to-indigo-500",
    success: "from-green-500 to-emerald-500"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl p-5 bg-gradient-to-r ${priorityStyles[nextStep.priority]} text-white shadow-lg mb-6`}
      data-testid="next-step-banner"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
            {nextStep.step ? (
              <nextStep.step.icon className="w-6 h-6" />
            ) : (
              <Sparkles className="w-6 h-6" />
            )}
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium mb-0.5">
              {nextStep.step ? `Siguiente Paso: ${nextStep.step.title}` : "¡Felicidades!"}
            </p>
            <p className="text-white font-semibold text-lg">
              {nextStep.message}
            </p>
          </div>
        </div>
        {nextStep.link && (
          <Link to={nextStep.link}>
            <Button 
              className="bg-white text-emerald-600 hover:bg-white/90 font-semibold shadow-md"
              data-testid="next-step-action-btn"
            >
              {nextStep.action}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </div>
    </motion.div>
  );
};

const ProgressChecklist = ({ caseData, ds160Progress, user, documentsCount, isExpanded, onToggle }) => {
  const isPremium = user?.plan_type === "premium" || user?.plan_type === "vip";

  // Calculate completion status for each step
  const getStepStatus = (step) => {
    if (step.alwaysComplete) return true;
    
    switch (step.id) {
      case "documents":
        return documentsCount > 0;
      case "ds160":
        return ds160Progress >= 100;
      case "interview_prep":
        return caseData?.interview_practiced || false;
      case "appointment":
        return caseData?.appointment_scheduled || false;
      default:
        return false;
    }
  };

  const completedSteps = JOURNEY_STEPS.filter(step => {
    if (step.premium && !isPremium) return false;
    return getStepStatus(step);
  }).length;

  const totalSteps = JOURNEY_STEPS.filter(step => !step.premium || isPremium).length;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-xl border border-navy-100 shadow-sm overflow-hidden"
      data-testid="progress-checklist"
    >
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-navy-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Target className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-navy-900">Tu Progreso</h3>
            <p className="text-sm text-navy-500">{completedSteps} de {totalSteps} pasos completados</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="text-2xl font-bold text-emerald-600">{progressPercent}%</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-navy-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-navy-400" />
          )}
        </div>
      </button>

      {/* Progress Bar */}
      <div className="px-5 pb-3">
        <Progress value={progressPercent} className="h-2" />
      </div>

      {/* Checklist Items */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-navy-100"
          >
            <div className="p-4 space-y-2">
              {JOURNEY_STEPS.map((step, index) => {
                // Skip premium steps for non-premium users
                if (step.premium && !isPremium) return null;
                
                const isComplete = getStepStatus(step);
                const Icon = step.icon;
                
                // Check if previous step is complete (to unlock this step)
                const previousStepComplete = index === 0 ? true : (() => {
                  // Find the previous non-premium step
                  let prevIndex = index - 1;
                  while (prevIndex >= 0) {
                    const prevStep = JOURNEY_STEPS[prevIndex];
                    if (prevStep.premium && !isPremium) {
                      prevIndex--;
                      continue;
                    }
                    return getStepStatus(prevStep);
                  }
                  return true;
                })();
                
                const isLocked = !isComplete && !previousStepComplete && index > 1;

                const handleStepClick = (e) => {
                  if (isLocked) {
                    e.preventDefault();
                    toast.error("Completa el paso anterior primero");
                  }
                };

                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      isComplete 
                        ? "bg-emerald-50" 
                        : isLocked
                        ? "bg-gray-100 opacity-60"
                        : "bg-navy-50 hover:bg-navy-100"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isComplete 
                        ? "bg-emerald-500 text-white" 
                        : isLocked
                        ? "bg-gray-300 text-gray-500"
                        : "bg-navy-200 text-navy-500"
                    }`}>
                      {isComplete ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : isLocked ? (
                        <Lock className="w-4 h-4" />
                      ) : (
                        <span className="text-sm font-semibold">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${
                        isComplete ? "text-emerald-700" 
                        : isLocked ? "text-gray-500"
                        : "text-navy-900"
                      }`}>
                        {step.title}
                        {step.premium && (
                          <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Premium
                          </span>
                        )}
                        {isLocked && (
                          <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                            🔒 Bloqueado
                          </span>
                        )}
                      </p>
                      <p className={`text-sm ${
                        isComplete ? "text-emerald-600" 
                        : isLocked ? "text-gray-400"
                        : "text-navy-500"
                      }`}>
                        {isComplete ? "✓ Completado" : isLocked ? "Completa el paso anterior" : step.description}
                      </p>
                    </div>
                    {/* Show button for incomplete steps */}
                    {!isComplete && step.link && (
                      isLocked ? (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-gray-400 border-gray-200 cursor-not-allowed"
                          onClick={handleStepClick}
                        >
                          <Lock className="w-3 h-3 mr-1" />
                          Bloqueado
                        </Button>
                      ) : (
                        <Link to={step.link}>
                          <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                            Ir
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      )
                    )}
                    {/* Show "Ver/Agregar" button for completed steps with links */}
                    {isComplete && step.link && (
                      <Link to={step.link}>
                        <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50">
                          {step.id === "documents" ? "Agregar más" : "Ver"}
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export { NextStepBanner, ProgressChecklist };
